import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  Typography
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';

import ErrorDisplay from '../common/ErrorDisplay';
import HanterResults from './HanterResults';
import SkinResults from './SkinResults';

const ResultsDisplay = ({ data, error, isLoading, onReset }) => {
  const [resultType, setResultType] = useState(null);
  const [hasHanterData, setHasHanterData] = useState(false);
  const [hasSkinData, setHasSkinData] = useState(false);
  const [isFromUrl, setIsFromUrl] = useState(false);
  
  // Определяем, пришли ли данные из URL
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    setIsFromUrl(!!queryParams.get('data'));
  }, []);
  
  // Определить наличие данных для каждого типа анализа
  useEffect(() => {
    if (data) {
      console.log('🔍 Анализ полученных данных в ResultsDisplay:', JSON.stringify(data, null, 2));
      
      // Проверка наличия данных о синдроме Хантера
      const hanterData = data.hanter_check_ru || data.hanter_check;
      const hasHanterRisk = !!(data.neuro_result_ru?.риск_хантера || data.neuro_result?.hanter_risk);
      
      // Проверка наличия данных о коже
      const hasSkinDiseasesRu = !!data.skin_diseases_ru;
      const hasSkinDiseases = !!data.skin_diseases;
      const hasSkinCheckRu = !!data.skin_check_ru;
      const hasSkinCheck = !!data.skin_check;
      const hasNeuroResultRu = !!data.neuro_result_ru;
      const hasNeuroResult = !!data.neuro_result;
      const hasSkinDiseaseRu = !!data.skin_disease_ru;
      const hasSkinDisease = !!data.skin_disease;
      
      const hanterDataExists = !!hanterData || hasHanterRisk;
      const skinData = hasSkinDiseasesRu || hasSkinDiseases || 
                       hasSkinCheckRu || hasSkinCheck || 
                       hasNeuroResultRu || hasNeuroResult ||
                       hasSkinDiseaseRu || hasSkinDisease;
      
      setHasHanterData(hanterDataExists);
      setHasSkinData(!!skinData);
      
      console.log('📊 Доступность данных:', {
        'Синдром Хантера': hanterDataExists,
        'Кожные заболевания': !!skinData,
        'skin_diseases_ru': hasSkinDiseasesRu,
        'skin_diseases': hasSkinDiseases,
        'skin_check_ru': hasSkinCheckRu,
        'skin_check': hasSkinCheck,
        'neuro_result_ru': hasNeuroResultRu,
        'neuro_result': hasNeuroResult,
        'skin_disease_ru': hasSkinDiseaseRu,
        'skin_disease': hasSkinDisease,
        'hanter_check_ru': !!data.hanter_check_ru,
        'hanter_check': !!data.hanter_check,
        'hanter_risk': hasHanterRisk
      });
      
      // Автоматически выбрать доступный тип результатов
      if (!resultType) {
        if (hanterDataExists && data.validation?.seven_years_old_boy_face) {
          console.log('🔄 Автоматический выбор типа: Синдром Хантера');
          setResultType('hanter');
        } else if (skinData && data.validation?.visible_skin_lesions) {
          console.log('🔄 Автоматический выбор типа: Кожные заболевания');
          setResultType('skin');
        } else if (hanterDataExists) {
          console.log('🔄 Автоматический выбор типа: Синдром Хантера (по умолчанию)');
          setResultType('hanter');
        } else if (skinData) {
          console.log('🔄 Автоматический выбор типа: Кожные заболевания (по умолчанию)');
          setResultType('skin');
        }
      }
    }
  }, [data, resultType]);
  
  // Обработчик переключения типа результатов
  const handleResultTypeChange = (event, newType) => {
    if (newType !== null) {
      console.log(`🔄 Пользователь переключил тип результатов на: ${newType === 'hanter' ? 'Синдром Хантера' : 'Кожные заболевания'}`);
      setResultType(newType);
    }
  };

  // Show loading state
  if (isLoading) {
    console.log('⏳ Отображение состояния загрузки...');
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexGrow: 1,
      }}>
        <CircularProgress size={40} thickness={3} color="primary" />
        <Box sx={{ mt: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
          Анализ фотографии...
        </Box>
      </Box>
    );
  }

  // Show error message if there is an error
  if (error) {
    console.log('❌ Отображение ошибки:', error);
    return (
      <Box sx={{ flexGrow: 1 }}>
        <ErrorDisplay message={error} />
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={onReset}
            startIcon={<RestartAltIcon />}
            size="small"
          >
            Начать заново
          </Button>
        </Box>
      </Box>
    );
  }

  // Return null if no data
  if (!data) return null;
  
  // Если нет данных обоих типов, показываем сообщение
  if (!hasHanterData && !hasSkinData) {
    console.log('ℹ️ Нет данных ни по одному из типов результатов');
    return (
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          Данные анализа не содержат результатов по синдрому Хантера или кожным заболеваниям.
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          onClick={onReset}
          startIcon={<RestartAltIcon />}
          size="small"
          sx={{ mt: 2 }}
        >
          Начать заново
        </Button>
      </Box>
    );
  }

  console.log(`📊 Отображение результатов типа: ${resultType === 'hanter' ? 'Синдром Хантера' : 'Кожные заболевания'}`);
  
  return (
    <Box sx={{ mt: 3, flexGrow: 1 }}>
      {/* Selector for result type - не показываем переключатель, если данные из URL */}
      {!isFromUrl && (hasHanterData && hasSkinData) && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Выберите тип результатов:
            </Typography>
          </Box>
          <ToggleButtonGroup
            value={resultType}
            exclusive
            onChange={handleResultTypeChange}
            aria-label="тип результатов"
            size="small"
          >
            <ToggleButton 
              value="hanter" 
              aria-label="синдром хантера" 
              disabled={!hasHanterData}
              sx={{ 
                px: 2, 
                py: 1,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  }
                }
              }}
            >
              <PersonIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
              Синдром Хантера
            </ToggleButton>
            <ToggleButton 
              value="skin" 
              aria-label="кожные заболевания" 
              disabled={!hasSkinData}
              sx={{ 
                px: 2, 
                py: 1,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  }
                }
              }}
            >
              <LocalHospitalIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
              Кожные заболевания
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
      
      {/* Показываем разделитель только если отображается переключатель */}
      {!isFromUrl && (hasHanterData && hasSkinData) && (
        <Divider sx={{ my: 2 }} />
      )}
      
      {/* Results based on selected type */}
      {resultType === 'hanter' && hasHanterData && (
        <HanterResults data={data} />
      )}
      
      {resultType === 'skin' && hasSkinData && (
        <SkinResults data={data} />
      )}
      
      {/* Reset button */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => {
            console.log('🔄 Пользователь нажал "Начать заново"');
            onReset();
          }}
          startIcon={<RestartAltIcon />}
          size="small"
        >
          Начать заново
        </Button>
      </Box>
    </Box>
  );
};

export default ResultsDisplay; 